import {
  Button,
  Icon,
  Tag,
  Text,
  Textarea,
} from "@ttc3k/trekker";
import { Box, Flex } from "styled-system/jsx";
import { Skeleton } from "shared/components/atoms/Skeleton";
import { ViewSubSection } from "shared/components/app";
import { useTranslation } from "react-i18next";
import {
  EnumSurveyQuestionType,
  type Maybe,
  type SurveyQuestion,
} from "gql/generated";
import { format } from "date-fns";
import { CheckCircleSolid, EditPencil, FloppyDisk } from "iconoir-react";
import { HtmlContent } from "shared/components/molecules/HtmlContent";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import {
  SurveyQuestionInput,
  formatBusinessScopedAnswer,
} from "shared/components/organisms/SurveyQuestionInput";
import { formatAddressSearchResponse } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import { getBooleanResponseDisplay } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";
import {
  isQuestionVisible,
  isToggleOn,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionVisibility";
import { getMultilingualString } from "shared/utils";
import type { BusinessAnswer } from "@shared/businessScope";

type SurveyResponseBodyProps = {
  surveyResponse: any;
  surveyResponseId: string | null;
  refetch: () => Promise<any>;
  isPendingReview: boolean;
  reviewNote: string;
  setReviewNote: (note: string) => void;
  onReview: (decision: "APPROVED" | "REJECTED") => void;
  reviewLoading: boolean;
  canEdit?: boolean;
  isEditing?: boolean;
  editedResponses?: {
    questionData: any;
    response: string[];
    byBusiness?: BusinessAnswer[];
  }[];
  onStartEdit?: () => void;
  onCancelEdit?: () => void;
  onEditResponseChange?: (questionId: string, response: string[]) => void;
  onSaveEdit?: () => void;
  editLoading?: boolean;
  organizationId?: string;
};

export const SurveyResponseBody = ({
  surveyResponse,
  isPendingReview,
  reviewNote,
  setReviewNote,
  onReview,
  reviewLoading,
  canEdit = false,
  isEditing = false,
  editedResponses = [],
  onStartEdit,
  onCancelEdit,
  onEditResponseChange,
  onSaveEdit,
  editLoading = false,
  organizationId,
}: SurveyResponseBodyProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "FORM_RESPONSE_MODAL",
  });
  const businesses: { operatorId: string; businessName?: string | null }[] =
    surveyResponse?.ownerBusinesses ?? [];
  const formatAnswer = (
    questionData: Parameters<typeof getBooleanResponseDisplay>[0] & {
      type?: string | null;
    },
    values: string[],
  ) => {
    const qType = questionData?.type;
    const rawValues = values.filter(Boolean);
    return qType === EnumSurveyQuestionType.Toggle
      ? isToggleOn(values) ? "Yes" : "No"
      : qType === EnumSurveyQuestionType.Boolean
        ? getBooleanResponseDisplay(questionData, rawValues)
        : qType === "WHEREABOUTS_SEARCH"
          ? rawValues[1] ?? rawValues[0] ?? "—"
          : qType === EnumSurveyQuestionType.AddressSearch
            ? formatAddressSearchResponse(rawValues)
            : rawValues.join(", ") || "—";
  };

  return (
    <>
      <ViewSubSection
        title={t("RESPONSES")}
        gap="200"
        buttonOverride={
          canEdit && !isEditing ? (
            <Button
              size="sm"
              mode="light"
              onClick={onStartEdit}
              type="button"
              Icon={EditPencil}
              aria-label={t("EDIT")}
            />
          ) : undefined
        }
      >
        <Skeleton alignItems="stretch" minH="unset">
          {isEditing ? (
            <Flex flexDirection="column" gap="300">
              {editedResponses.map((r, idx) => {
                if (!isQuestionVisible(r.questionData, editedResponses)) return null;
                const perBusiness = formatBusinessScopedAnswer(
                  r.questionData,
                  r,
                  editedResponses,
                  businesses,
                  answer => formatAnswer(r.questionData, answer),
                );
                if (perBusiness != null) {
                  return (
                    <Box key={r.questionData._id ?? idx}>
                      <Text visual="smallMedium" color="text.mid" mb="50">
                        {getMultilingualString(r.questionData.name ?? {}) || "—"}
                      </Text>
                      <Text visual="bodyRegular" color="text.dark">
                        {perBusiness}
                      </Text>
                    </Box>
                  );
                }
                return (
                <SurveyQuestionInput
                  key={r.questionData._id ?? idx}
                  question={r.questionData as SurveyQuestion}
                  response={r.response as (string | Maybe<string>)[]}
                  onResponseChange={onEditResponseChange!}
                  organizationId={organizationId}
                />
                );
              })}
              <Flex gap="150" justifyContent="flex-end">
                <Button
                  mode="light"
                  onClick={onCancelEdit}
                  disabled={editLoading}
                  type="button"
                >
                  {t("CANCEL_EDIT")}
                </Button>
                <Button
                  visual="accent.blue"
                  onClick={onSaveEdit}
                  isLoading={editLoading}
                  disabled={editLoading}
                  type="button"
                >
                  {t("SAVE_EDIT")}
                </Button>
              </Flex>
            </Flex>
          ) : (
            <Flex flexDirection="column" gap="200">
              {surveyResponse?.responses?.map((r: any, idx: number) => {
                if (!r?.questionData) return null;
                const questionLabel = getMultilingualString(
                  r.questionData.name ?? {},
                );
                const qType = r.questionData.type;
                const richHtml = (r.response ?? [])[0] ?? "";
                const isRichText = qType === EnumSurveyQuestionType.RichText;
                const isSkipped = !isQuestionVisible(
                  r.questionData,
                  surveyResponse.responses,
                );
                const answer = isSkipped
                  ? t("SKIPPED")
                  : formatBusinessScopedAnswer(
                      r.questionData,
                      r,
                      surveyResponse.responses,
                      businesses,
                      values => formatAnswer(r.questionData, values),
                    ) ?? formatAnswer(r.questionData, r.response ?? []);
                return (
                  <Box key={idx}>
                    <Text visual="smallMedium" color="text.mid" mb="50">
                      {questionLabel || "—"}
                    </Text>
                    {isRichText ? (
                      !isLexicalHtmlEmpty(richHtml) ? (
                        <HtmlContent html={richHtml} />
                      ) : (
                        <Text visual="bodyRegular" color="text.dark">
                          —
                        </Text>
                      )
                    ) : (
                      <Text visual="bodyRegular" color="text.dark">
                        {answer}
                      </Text>
                    )}
                  </Box>
                );
              })}
            </Flex>
          )}
        </Skeleton>
      </ViewSubSection>

      {(surveyResponse as any)?.history?.length > 0 && (() => {
        const reversed = [...(surveyResponse as any).history].reverse();
        return (
          <ViewSubSection title={t("HISTORY")} gap="200">
            <Flex flexDirection="column" gap="200">
              {reversed.map((entry: any, idx: number) => {
                const isSave = entry.action === "SAVE";
                const isAdminEdit = entry.action === "ADMIN_EDIT";
                return (
                  <Flex
                    key={idx}
                    flexDir="column"
                    gap="50"
                    pb="150"
                    borderBottom={idx < reversed.length - 1 ? "1px solid" : undefined}
                    borderColor="border.light"
                  >
                    <Flex alignItems="center" gap="100" justifyContent="space-between">
                      <Flex alignItems="center" gap="100">
                        <Icon
                          Element={
                            isAdminEdit
                              ? EditPencil
                              : isSave
                                ? FloppyDisk
                                : CheckCircleSolid
                          }
                          color={
                            isAdminEdit
                              ? "icon.mid"
                              : isSave
                                ? "icon.mid"
                                : "icon.success.mid"
                          }
                          size="xs"
                        />
                        <Text visual="smallMedium" color="text.dark">
                          {isAdminEdit
                            ? t("ACTION_ADMIN_EDIT")
                            : isSave
                              ? t("ACTION_SAVE")
                              : t("ACTION_SUBMIT")}
                        </Text>
                        {entry.personName && (
                          <Text visual="smallRegular" color="text.light">
                            by {entry.personName}
                          </Text>
                        )}
                      </Flex>
                      <Text visual="smallRegular" color="text.light">
                        {entry.timestamp
                          ? format(new Date(entry.timestamp), "MMM d, yyyy h:mm a")
                          : ""}
                      </Text>
                    </Flex>
                    {entry.reviewStatus && (
                      <Flex alignItems="center" gap="100" ml="400">
                        <Tag
                          visual={
                            entry.reviewStatus === "APPROVED"
                              ? "success"
                              : entry.reviewStatus === "REJECTED"
                                ? "destructive"
                                : "highlight"
                          }
                          mode="light"
                        >
                          {entry.reviewStatus === "APPROVED"
                            ? t("REVIEW_APPROVED")
                            : entry.reviewStatus === "REJECTED"
                              ? t("REVIEW_REJECTED")
                              : t("REVIEW_PENDING")}
                        </Tag>
                      </Flex>
                    )}
                    {entry.reviewNote && (
                      <Text visual="smallRegular" color="text.light" ml="400">
                        {entry.reviewNote}
                      </Text>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          </ViewSubSection>
        );
      })()}

      {isPendingReview && (
        <ViewSubSection title={t("REVIEW_ACTIONS")} gap="200">
          <Flex flexDirection="column" gap="200">
            <Textarea
              value={reviewNote}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setReviewNote(e.target.value)
              }
              placeholder={t("REVIEW_NOTE_PLACEHOLDER")}
              rows={3}
            />
            <Flex gap="150">
              <Button
                visual="accent.blue"
                onClick={() => onReview("APPROVED")}
                isLoading={reviewLoading}
                disabled={reviewLoading}
                type="button"
              >
                {t("APPROVE")}
              </Button>
              <Button
                visual="destructive"
                mode="light"
                onClick={() => onReview("REJECTED")}
                isLoading={reviewLoading}
                disabled={reviewLoading}
                type="button"
              >
                {t("REJECT")}
              </Button>
            </Flex>
          </Flex>
        </ViewSubSection>
      )}
    </>
  );
};
